import React, { useEffect, useState, useCallback, useRef } from "react";
import useFetchExecutionHistory from "../../hooks/useFetchExecutionHistory";
import { GridColDef, GridExpandMoreIcon } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { Grid, IconButton, Tooltip, CircularProgress } from "@mui/material";
import {
  downloadRulesHistoryFile,
  getFormatedDate,
  getFormattedDateTime,
  getFormattedDateTimeWithAMPM,
  getFormattedTime,
  normalizeJobStatus,
  useToast,
} from "../../services/utils";
import {
  downloadFileSecurely,
  getSecureValidationReportPath,
} from "../../services/fileDownloadService";
import EHistoryDataTable from "../../components/DataGrids/EHistoryDataGrid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import Loader from "../../components/Molecules/Loader/Loader";

import {
  IconDeleteBlueSvg,
  IconEyeBase,
  IconReRunBlue,
  IconAbortBlue,
  IconViewErrorSvg,
  IconDownloadBlueSvg,
} from "../../common/utils/icons";
import ExecutionModeIcon from "../../components/Molecules/ExecutionMode/ExecutionModeIcon";
// import useFetchIncidentByExecutionId from "../../hooks/useFetchIncidentByExecutionId";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ConsolidatedReportsFilter from "../../components/Molecules/FilterSearch/ConsolidatedReportsFilter";
import { deleteComparisonExecutionbyID } from "../../services/rulesService";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";
import DashboardJobstatusIcon from "../../common/utils/DashboardJobstatusIcon";
import useSyncNotifications from "../../hooks/useSyncNotifications";
// import useWebSocket from "../../hooks/useWebSocket"; // No longer needed
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { getExecutionSubGroupName } from "../../utils/executionGroup";
import useExecutionSubGroupMap from "../../hooks/useExecutionSubGroupMap";
import useFetchResearchQueryHistory from "../../hooks/useFetchResearchQueryHistory";
import {
  deleteExecutionResultByExecutionId,
  abortResearchQueryExecution,
  getResearchQueryByExecutionId,
} from "../../services/ResearchQueriesService";
import { useAuth } from "../../contexts/AuthContext";
import ErrorPopover from "../../components/Popovers/ErrorPopover";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import ScoreBreakdownDialog from "../../components/Scoring/ScoreBreakdownDialog";
import { getGenericRQScore } from "../../services/scoringService";
import { IGenericRQScore } from "../../types/Scoring.type";

/**
 * Inline cell component that displays a GRQ Execution Score badge using score data
 * from the history API response (no separate API call per row).
 * Full score details are fetched on-demand only when the breakdown dialog is opened.
 */
const ExecutionScoreCell: React.FC<{
  executionId: number;
  jobStatus: string;
  score: number | null | undefined;
  scoreCalculatedOn: string | null | undefined;
}> = ({ executionId, jobStatus, score, scoreCalculatedOn }) => {
  const [fullScoreData, setFullScoreData] =
    React.useState<IGenericRQScore | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogLoading, setDialogLoading] = React.useState(false);

  const normalizedStatus = jobStatus?.toLowerCase();
  const isCompleted =
    normalizedStatus === "completed" ||
    normalizedStatus === "completed_with_errors";

  if (!isCompleted || score == null) {
    return <ScoreBadge score={null} size="small" />;
  }

  const handleBadgeClick = async () => {
    setDialogOpen(true);
    if (!fullScoreData && executionId) {
      setDialogLoading(true);
      try {
        const data = await getGenericRQScore(executionId);
        setFullScoreData(data || null);
      } catch {
        /* ignore */
      } finally {
        setDialogLoading(false);
      }
    }
  };

  return (
    <>
      <ScoreBadge
        score={score}
        size="small"
        onClick={handleBadgeClick}
        tooltipText="Click to view score breakdown"
      />
      <ScoreBreakdownDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        type="generic_rq"
        scoreData={fullScoreData}
        loading={dialogLoading}
      />
    </>
  );
};

interface ResearchQueryHistoryData {
  domainId: number | null;
  ruleId: number | null;
  fromDate: string | undefined;
}
interface RuleSearchData {
  search_by: string | null;
  search_query: string | null;
  search_date?: string;
}

// Centralized Filter Configuration for Generic Research Execution History
// Note: Field names match the internal state. The service layer (ResearchQueriesService.tsx)
// handles transformation to API parameter names:
// - generic_execution_id → ids (API parameter)
// - generic_research_code → generic_research_query_code (API parameter)
// - run_id is stored when user selects from run_list dropdown (AutoCompleteRunList)
const GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG = {
  // Default filter state - single source of truth
  // Note: Using start_date in UI/state, but it will be transformed to 'date' parameter for API
  DEFAULT_STATE: {
    start_date: null,
    generic_research_query_id: null,
    generic_research_code: "",
    run_id: null, // Stores the selected run's ID from run_list dropdown
    execution_status: "",
    generic_execution_id: null,
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["generic_research_query_id", "run_id", "generic_execution_id"],
    STRING_FIELDS: ["generic_research_code", "execution_status"],
    DATE_FIELDS: ["start_date"],
  },
} as const;

const GenericResearchExecutionHistory = () => {
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const queryParams = new URLSearchParams(window.location.search);

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const executionSubGroupMap = useExecutionSubGroupMap();
  const [isloading, setIsLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track if this is the initial mount
  const isInitialMount = useRef(true);

  // Ref to prevent circular updates between URL and state
  const isUpdatingFromURL = useRef(false);

  // Permission checks for research query execution history actions
  const canViewExecutionHistory = hasPermission(
    "generic_research_query_result",
    "view",
  );
  const canDeleteExecutionHistory = hasPermission(
    "generic_research_query_result",
    "delete",
  );
  const canExecuteResearchQuery = hasPermission(
    "generic_research_query",
    "execute",
  );
  const [isBackdropLoading, setIsBackdropLoading] = useState<boolean>(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>({
    items: [],
    total: 0,
    page: 0,
    size: 0,
    pages: 0,
  });
  const [expandedRow, setExpandedRow] = useState<any>([]);
  const [formData, setFormData] = useState({
    run_name: "",
    run_date: dayjs().subtract(1, "day"),
    report_type: "",
  });
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // Helper function to get filter state from URL (for initialization and URL changes)
  const getInitialFilterState = useCallback(() => {
    const defaultFilterState = {
      ...GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Handle ID fields (convert to number if present)
    GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value && !isNaN(Number(value))) {
          urlFilters[field] = Number(value);
        }
      },
    );

    // Handle string fields
    GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value) {
          urlFilters[field] = value;
        }
      },
    );

    // Handle date fields
    GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value) {
          // Validate date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateRegex.test(value)) {
            urlFilters[field] = value;
          }
        }
      },
    );

    return { ...defaultFilterState, ...urlFilters };
  }, [searchParams]);

  // Initialize filter state from URL on mount
  const [searchFilterData, setSearchFilterData] = useState<any>(() =>
    getInitialFilterState(),
  );

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = useCallback(
    (filters: any) => {
      const newParams = new URLSearchParams(searchParams);

      // Remove all existing filter parameters first
      GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.URL_FIELDS.forEach(
        (field) => {
          newParams.delete(field);
        },
      );

      // Add non-empty filter values to URL
      GENERIC_RESEARCH_EXECUTION_HISTORY_FILTER_CONFIG.URL_FIELDS.forEach(
        (field) => {
          const value = filters[field];
          if (value !== null && value !== undefined && value !== "") {
            if (typeof value === "string") {
              newParams.set(field, value);
            } else {
              newParams.set(field, String(value));
            }
          }
        },
      );

      return newParams;
    },
    [searchParams],
  );

  const [isConsolidatedFilterVisible, setIsConsolidatedFilterVisible] =
    useState(false);

  // const [incidentData, setIncidentData] = useState<any>([]);
  // const [incidentResultIds, setIncidentResultIds] = useState();

  const [
    openComparisonExecutionConfirmation,
    setOpenComparisonExecutionConfirmation,
  ] = useState(false);

  const [openAbortExecutionConfirmation, setOpenAbortExecutionConfirmation] =
    useState(false);

  const [deleteExecutionId, setDeleteExecutionId] = useState<number | null>(
    null,
  );

  const [abortExecutionId, setAbortExecutionId] = useState<number | null>(null);

  const [
    openValidationExecutionConfirmation,
    setOpenValidationExecutionConfirmation,
  ] = useState(false);
  const [selectedQueryToDelete, setSelectedQueryToDelete] = useState<any>(null);

  // Error popover state
  const [errorAnchorEl, setErrorAnchorEl] = useState<null | HTMLElement>(null);
  const [errorData, setErrorData] = useState<any>(null);
  const [isErrorLoading, setIsErrorLoading] = useState(false);

  // Download state management for output logs
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );
  const [downloadProgress, setDownloadProgress] = useState<
    Map<string, { percentage: number; loaded: number; total: number }>
  >(new Map());

  const [researchQueryHistoryParams, setResearchQueryHistoryParams] =
    useState<ResearchQueryHistoryData>({
      domainId: Number(queryParams.get("domainId")),
      ruleId: Number(queryParams.get("ruleId")),
      fromDate: queryParams.get("fromDate")?.toString(),
    });
  const [researchQueryList] = useFetchResearchQueryHistory({
    setIsLoading: setIsBackdropLoading,
    page,
    pSize,
    domainId: researchQueryHistoryParams.domainId,
    ruleId: researchQueryHistoryParams.ruleId,
    fromDate: researchQueryHistoryParams.fromDate,
    searchFilterData,
  });

  // Get handleStorageChange from useSyncNotifications
  // This will automatically sync with notifications from the context
  useSyncNotifications(setFileData, fileData);

  const handleRowExpand = async (rowId: number) => {
    const result = await getResearchQueryByExecutionId({
      currentResearchQueryResultId: rowId,
    });
    return result;
  };
  // const [fetchIncidentData] = useFetchIncidentByExecutionId({
  //   setIsLoading,
  //   data: incidentResultIds,
  // });
  // useEffect(() => {
  //   setIncidentData(fetchIncidentData);
  // }, [fetchIncidentData, page]);

  // Update URL when filter state changes (State → URL)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(searchFilterData);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, serializeFiltersToURL, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → State) - for back/forward navigation
  useEffect(() => {
    // Extract current filter values from URL
    const urlFilterState = getInitialFilterState();

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes
    if (hasFilterChanges) {
      // Set flag to prevent the URL update effect from running
      isUpdatingFromURL.current = true;
      setSearchFilterData(urlFilterState);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 0);
    }
  }, [getInitialFilterState]);

  useEffect(() => {
    if (researchQueryList?.items?.length >= 0) {
      // const resultIdArr = researchQueryList?.items?.map((row: any) => row.id);
      // setIncidentResultIds(resultIdArr);
      // Normalize job_status to lowercase for all items
      const normalizedData = {
        ...researchQueryList,
        items: researchQueryList.items.map(normalizeJobStatus),
      };
      setFileData(normalizedData);
    }
  }, [researchQueryList]);

  // useEffect(() => {
  //   const updatedFileData = handleRuleExecutionList(executionList);
  //   // Update fileData state
  //   if (updatedFileData?.items?.length > 0) {
  //     const newData = updatedFileData.items.map((file: any) => {
  //       const fileItem: any =
  //         incidentData &&
  //         incidentData.length > 0 &&
  //         incidentData.find((incidentItem: any) => {
  //           return Number(incidentItem?.ExecutionId) === Number(file.id);
  //         });
  //       if (fileItem) {
  //         return {
  //           ...file,
  //           IncidentStatus: fileItem?.IncidentStatus,
  //         };
  //       }
  //       return file;
  //     });
  //     setFileData({ ...updatedFileData, items: newData });
  //   }
  // }, [incidentData, page]);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedNotifications = localStorage.getItem("notifications");

      if (storedNotifications && fileData?.items?.length > 0) {
        try {
          const notifications = JSON.parse(storedNotifications);

          setFileData((prevFileData: any) => {
            const updatedItems = prevFileData.items
              .map((item: any) => {
                const matchingNotification = notifications.find(
                  (notification: any) =>
                    (notification?.execution_type ===
                      "generic_research_query" ||
                      notification?.execution_type ===
                        "generic_research_query_rerun") &&
                    notification?.execution_id === item?.id,
                );

                // if (matchingNotification?.job_status === "failed") {
                //   return null; // Remove this item
                // }

                if (matchingNotification?.job_status) {
                  return {
                    ...item,
                    job_status: matchingNotification.job_status.toLowerCase(),
                  };
                }

                return item;
              })
              .filter(Boolean); // Remove nulls (failed items)

            const hasChanges =
              updatedItems.length !== prevFileData.items.length ||
              updatedItems.some(
                (newItem: any, index: number) =>
                  newItem.job_status !== prevFileData.items[index]?.job_status,
              );

            return hasChanges
              ? { ...prevFileData, items: updatedItems }
              : prevFileData;
          });
        } catch (error) {
          console.error(
            "Error parsing notifications from localStorage:",
            error,
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    const checkInterval = setInterval(handleStorageChange, 1000);
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(checkInterval);
    };
  }, [researchQueryList]);

  // Download functionality for output logs
  const handleSecureDownload = useCallback(
    async (filepath: string, fileName: string) => {
      if (downloadingFiles.has(filepath)) return; // Prevent duplicate downloads

      setDownloadingFiles((prev) => new Set(prev).add(filepath));
      setDownloadProgress((prev) =>
        new Map(prev).set(filepath, { percentage: 0, loaded: 0, total: 0 }),
      );

      try {
        await downloadFileSecurely(
          filepath,
          fileName,
          true, // isExtractAndEncodeFilePath
          (progress) => {
            // Update progress for this specific file
            setDownloadProgress((prev) =>
              new Map(prev).set(filepath, progress),
            );
          },
        );
      } catch (error) {
        console.error(`❌ Enhanced download failed for ${fileName}:`, error);
        // Error handling is done in the downloadFileSecurely function
      } finally {
        setDownloadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(filepath);
          return newSet;
        });
        setDownloadProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(filepath);
          return newMap;
        });
      }
    },
    [downloadingFiles],
  );

  const getSecureReportPath = useCallback(
    (path: string) => {
      if (path) {
        const { fileName } = getSecureValidationReportPath(path);
        return {
          downloadAction: () => handleSecureDownload(path, fileName),
          fileName,
        };
      }
      return null;
    },
    [handleSecureDownload],
  );

  /**
   * Set table coloums for execution history
   */
  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params: any) => <></>,
    },
    {
      field: "id",
      headerName: "Execution ID",

      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return (
          <Tooltip
            title={`Execution Group Id: ${params?.row?.run_id}`}
            placement="top"
            arrow
          >
            <span>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "run_id",
      headerName: "Execution Group ID",

      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "run_name",
      headerName: "Execution Group Name",
      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "execution_sub_group_id",
      headerName: "Execution Sub Group ID",
      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "execution_sub_group_code",
      headerName: "Execution Sub Group Name",
      minWidth: 90,
      groupable: false,
      renderCell: (params: any) => {
        return (
          <LimitChractersWithTooltip
            value={getExecutionSubGroupName(params?.row, executionSubGroupMap)}
          />
        );
      },
    },
    {
      field: "generic_research_query_code",
      headerName: "Research Query Code",
      flex: 1,
      minWidth: 120,
      groupable: false,
      renderCell: (params: any) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "execution_name",
      headerName: "Execution Name",
      flex: 1,
      minWidth: 120,
      groupable: false,
      // renderCell: (params: any) => {
      //   return <LimitChractersWithTooltip value={params?.value} />;
      // },
    },

    {
      field: "execution_time",
      headerName: "Execution Date & Time",
      flex: 1,
      minWidth: 180,
      groupable: false,
      renderCell: (params) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const executionTime = params?.value?.execution_time || params.value;
        const formattedTime = getFormattedDateTimeWithAMPM(executionTime);
        return (
          <div>
            <div>{formattedTime}</div>
          </div>
        );
      },
    },
    {
      field: "job_status",
      headerName: "Execution Status",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      groupable: false,
      renderCell: (params: any) => (
        <DashboardJobstatusIcon status={params?.row?.job_status} />
      ),
    },
    {
      field: "execution_mode",
      headerName: "Execution Mode",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      groupable: false,
      renderCell: (params: any) => (
        <ExecutionModeIcon executionMode={params?.row?.execution_mode} />
      ),
    },
    {
      field: "execution_score",
      headerName: "Success Rate",
      flex: 0.7,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      groupable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <ExecutionScoreCell
          executionId={params?.row?.id}
          jobStatus={params?.row?.job_status}
          score={params?.row?.score}
          scoreCalculatedOn={params?.row?.score_calculated_on}
        />
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "left",
      headerAlign: "left",
      minWidth: 120,
      groupable: false,
      renderCell: (params) => {
        if (params.rowNode.type === "group") {
          return null;
        }
        const handleViewClick = (incidentTab?: any) => {
          const basePath = `/research-execution-history/${params?.row?.generic_research_query_id}/${params?.row.id}/dashboard`;

          const navigatePath = incidentTab
            ? `${basePath}#incidentTab`
            : basePath;

          navigate(navigatePath, {
            state: {
              rowData: params?.row,
            },
          });
        };
        const handleReRunClick = () => {
          navigate(
            `/research-execution-history/re-run-research-query-execution/${params?.row?.generic_research_query_id}?re-run=true&execution-id=${params?.row?.id}&run-id=${params?.row?.run_id}&run-name=${params?.row?.run_name}&execution-sub-group-id=${params?.row?.execution_sub_group_id}&execution-sub-group-code=${params?.row?.execution_sub_group_code}`,
            {
              state: {
                rowData: params?.row,
                isReRun: true,
              },
            },
          );
        };
        const handleAbortExecution = () => {
          setAbortExecutionId(params?.row?.id);
          setOpenAbortExecutionConfirmation(true);
        };

        const handleViewError = async (
          event: React.MouseEvent<HTMLButtonElement>,
        ) => {
          // Persist the event target before async operations
          const target = event.currentTarget;

          // Set anchor immediately to show popover
          setErrorAnchorEl(target);
          setIsErrorLoading(true);
          setErrorData(null);

          try {
            // Map the research query error data to the format expected by ErrorPopover
            const errorInfo = {
              job_status: params?.row?.job_status || "failed",
              exception_message:
                params?.row?.exception_message ||
                params?.row?.error_message ||
                "Execution failed",
              log_file:
                params?.row?.output_logs ||
                params?.row?.result_files?.output_logs ||
                null,
            };
            setErrorData(errorInfo);
          } catch (error) {
            console.error("Error fetching error details:", error);
            setErrorData({
              job_status: "failed",
              exception_message: "Failed to load error details",
              log_file: null,
            });
          } finally {
            setIsErrorLoading(false);
          }
        };
        return (
          <>
            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors") &&
              canViewExecutionHistory && (
                <Tooltip title="View Dashboard" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleViewClick()}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
              )}

            {(params?.row?.job_status === "in_progress" ||
              params?.row?.job_status === "in_queue") &&
              // && params?.row?.is_running_in_background
              canDeleteExecutionHistory && (
                <Tooltip title="Abort Execution" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleAbortExecution()}
                  >
                    <IconAbortBlue />
                  </IconButton>
                </Tooltip>
              )}
            {(params?.row?.job_status === "failed" ||
              params?.row?.job_status === "aborted") &&
              canViewExecutionHistory && (
                <Tooltip title="View Error" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={handleViewError}
                  >
                    <IconViewErrorSvg />
                  </IconButton>
                </Tooltip>
              )}
            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors") &&
              canExecuteResearchQuery && (
                <Tooltip title="Re-Run Research Query" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={handleReRunClick}
                  >
                    <IconReRunBlue />
                  </IconButton>
                </Tooltip>
              )}

            {(params?.row?.job_status === "completed" ||
              params?.row?.job_status === "completed_with_errors" ||
              params?.row?.job_status === "failed" ||
              params?.row?.job_status === "aborted") &&
              canDeleteExecutionHistory && (
                <Tooltip title="Delete" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      setSelectedQueryToDelete(params.row);
                      setOpenValidationExecutionConfirmation(true);
                    }}
                  >
                    <IconDeleteBlueSvg />
                  </IconButton>
                </Tooltip>
              )}
            {/* Conditionally render download button only when output logs are available */}
            {(params?.row?.output_logs ||
              params?.row?.result_files?.output_logs) &&
              (() => {
                const outputLogsPath =
                  params?.row?.output_logs ||
                  params?.row?.result_files?.output_logs;
                const isDownloading = downloadingFiles.has(outputLogsPath);
                const reportPath = getSecureReportPath(outputLogsPath);

                return (
                  <Tooltip
                    title={
                      isDownloading ? "Downloading..." : "Download Output Logs"
                    }
                    placement="top"
                    arrow
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        disabled={isDownloading}
                        onClick={() => {
                          if (!isDownloading && reportPath) {
                            reportPath.downloadAction();
                          }
                        }}
                        sx={{
                          "& svg": {
                            width: "20px",
                            height: "20px",
                            fill: isDownloading
                              ? "var(--auto-gray)"
                              : "var(--auto-green)",
                          },
                          cursor: isDownloading ? "not-allowed" : "pointer",
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "var(--auto-green)" }}
                          />
                        ) : (
                          <IconDownloadBlueSvg />
                        )}
                      </IconButton>
                      {/* Progress percentage display */}
                      {isDownloading &&
                        downloadProgress.has(outputLogsPath) && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "var(--auto-green)",
                              color: "white",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {downloadProgress.get(outputLogsPath)?.percentage ||
                              0}
                            %
                          </Box>
                        )}
                    </Box>
                  </Tooltip>
                );
              })()}
          </>
        );
      },
    },
  ];

  const handleSubmitReport = async (event: any) => {
    // event.preventDefault();
    navigate(
      `/rules-execution-history/consolidate-results?runName=${formData.run_name}&reportType=${formData.report_type}&runDate=${formData?.run_date}`,
    );
  };

  const handleDelete = async () => {
    if (deleteExecutionId) {
      try {
        setIsDeleteLoading(true);
        await deleteComparisonExecutionbyID(deleteExecutionId);
        const deletedRow = fileData.items.find(
          (item: any) => item.id === deleteExecutionId,
        );
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== deleteExecutionId,
          ),
        }));
        showToast(`${deleteExecutionId} deleted successfully`, "success");
      } catch (error: any) {
        console.error("Error deleting execution:", error);
        showToast(error?.message || "Failed to delete execution", "error");
      } finally {
        setIsDeleteLoading(false);
      }
    }
  };

  const handleValidationExecutionDelete = async () => {
    setOpenValidationExecutionConfirmation(false);
    if (!selectedQueryToDelete) return;
    setIsLoading(true);
    try {
      await deleteExecutionResultByExecutionId(selectedQueryToDelete.id);
      const updatedList = fileData?.items?.filter(
        (item: any) => item.id !== selectedQueryToDelete.id,
      );
      setFileData((prev: any) => ({
        ...prev,
        items: updatedList,
      }));

      showToast(
        `${selectedQueryToDelete.generic_research_query_code} deleted successfully`,
        "success",
      );
    } catch (error) {
      console.error("Error deleting query:", error);
      showToast("Failed to delete the execution. Please try again.", "error");
    } finally {
      setIsLoading(false);
      setSelectedQueryToDelete(null);
    }
  };

  const handleAbortExecution = async () => {
    if (abortExecutionId) {
      try {
        setIsDeleteLoading(true);
        const result = await abortResearchQueryExecution(abortExecutionId);
        if (result?.status === 200) {
          setFileData((prev: any) => ({
            ...prev,
            items:
              prev?.items?.map((item: any) =>
                item.id === abortExecutionId
                  ? { ...item, job_status: "aborted" }
                  : item,
              ) || [],
          }));
          showToast(`${abortExecutionId} aborted successfully`, "success");
        }
      } catch (error) {
        console.error("Error aborting execution:", error);
      } finally {
        setIsDeleteLoading(false);
      }
    }
  };

  const handleAbortExecutionDelete = () => {
    setOpenAbortExecutionConfirmation(false);
    handleAbortExecution();
  };

  return (
    <>
      {(isBackdropLoading || isDeleteLoading) && <Loader isLoading={true} />}
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={[
                "start_date",
                "generic_research_query_id",
                "generic_research_code",
                "run_list",
                "execution_sub_group_list",
                "execution_status",
                "generic_execution_id",
              ]}
              primaryFields={[
                "generic_research_query_id",
                "generic_research_code",
                "generic_execution_id",
              ]}
              fieldRequirements={{
                dateTitle: "From Date",
                tooltipRequired: true,
                includeRunId: true,
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <ConsolidatedReportsFilter
        setFormData={setFormData}
        isFilterVisible={isConsolidatedFilterVisible}
        setIsFilterVisible={setIsConsolidatedFilterVisible}
        formData={formData}
        handleSubmitReport={handleSubmitReport}
      />
      <EHistoryDataTable
        dataColumns={columns || []}
        dataRows={fileData?.items}
        loading={isloading}
        setLoading={setIsLoading}
        dataListTitle={"Research Query History"}
        isExportButtonRequired={true}
        className="dataTable no-radius hide-progress-icon"
        rowCount={fileData?.total}
        paginationModel={{
          page: page - 1,
          pageSize: pSize,
        }}
        onPaginationModelChange={(params: any) => {
          if (params.pageSize !== pSize || params.page !== page - 1) {
            setPage(params.page + 1);
            setPSize(params.pageSize);
          }
        }}
        historyType={"Execution"}
        // downloadFromAPIFile={() =>
        //   downloadRulesHistoryFile({
        //     setLoading: setIsBackdropLoading,
        //     searchFilterData,
        //   })
        // }
        disableColumnFilter={false}
        rowGroupByColumnProps={enableRowGroupByColumnProps}
        fitViewport
        singlePageMaxHeightDiff={335}
        isShowInComparisonHistory={true}
        onRowExpand={handleRowExpand}
      />
      <ConfirmationDialog
        title={"Confirm removing Execution"}
        dialogContent={"Are you sure you want to delete this execution?"}
        handleCancel={() => setOpenValidationExecutionConfirmation(false)}
        openConfirmation={openValidationExecutionConfirmation}
        handleConfirm={handleValidationExecutionDelete}
        isShowClassMb0={false}
      />
      <ConfirmationDialog
        title={"Confirm Abort Execution"}
        dialogContent={
          "Are you sure you want to abort this research query execution?"
        }
        handleCancel={() => setOpenAbortExecutionConfirmation(false)}
        openConfirmation={openAbortExecutionConfirmation}
        handleConfirm={handleAbortExecutionDelete}
        isShowClassMb0={false}
      />
      <ErrorPopover
        open={Boolean(errorAnchorEl)}
        anchorEl={errorAnchorEl}
        onClose={() => {
          setErrorAnchorEl(null);
          setErrorData(null); // Clear error data when closing
        }}
        errorData={errorData}
        isLoading={isErrorLoading}
        title="Research Query Execution Error Details"
      />
    </>
  );
};

export default GenericResearchExecutionHistory;
